import { observable, action, computed, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { validationContext, required, func } from 'validx'
import { type Team } from '@/features/iam/teams/Team'
import { type TeamStore } from '@/features/iam/teams/TeamStore'
import { type GetCurrentMemberFn } from '@/features/iam'
import { populateValidationContextFromError } from '@/utils/validx-util'
import { getFieldErrors } from '@/utils/error-util'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'

/**
 * State used for editing team info.
 */
export class TeamDetailsViewModel {
  @observable accessor name = ''
  @observable accessor slug = ''
  @observable accessor teamPurpose = ''
  @observable accessor streetAddress = ''
  @observable accessor city = ''
  @observable accessor state = ''
  @observable accessor zipCode = ''
  @observable accessor country = ''

  readonly validation = validationContext(this, {
    name: [
      required('Team name is required'),
      func(({ value }: { value: string }) => {
        return value.length <= 50
      }, "Name can't be more than 50 characters"),
    ],
  })

  constructor(
    public readonly team: Team,
    private readonly _teamStore: TeamStore,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Fills the form from the team.
   */
  @action.bound
  activate() {
    this.name = this.team.name
    this.slug = this.team.slug
    this.teamPurpose = this.team.teamPurpose
    this.streetAddress = this.team.address?.streetAddress ?? ''
    this.zipCode = this.team.address?.zipCode ?? ''
    this.city = this.team.address?.city ?? ''
    this.state = this.team.address?.state ?? ''
    this.country = this.team.address?.country ?? ''
  }

  @computed
  get hasChanges() {
    return (
      this.name !== this.team.name ||
      this.slug !== this.team.slug ||
      this.teamPurpose !== this.team.teamPurpose ||
      this.streetAddress !== (this.team.address?.streetAddress ?? '') ||
      this.city !== (this.team.address?.city ?? '') ||
      this.state !== (this.team.address?.state ?? '') ||
      this.zipCode !== (this.team.address?.zipCode ?? '') ||
      this.country !== (this.team.address?.country ?? '')
    )
  }

  @computed
  get canEditDetails() {
    const currentMember = this._getCurrentMember()
    return this.team.owner.member.user.id === currentMember.user.id
  }

  /**
   * Saves the team info.
   */
  saveInfo = task.resolved(async () => {
    runInAction(() => {
      this.name = this.name ? this.name.trim() : ''
      this.teamPurpose = this.teamPurpose ? this.teamPurpose.trim() : ''
      this.streetAddress = this.streetAddress ? this.streetAddress.trim() : ''
      this.city = this.city ? this.city.trim() : ''
      this.state = this.state ? this.state.trim() : ''
      this.zipCode = this.zipCode ? this.zipCode.trim() : ''
      this.country = this.country ? this.country.trim() : ''
    })

    if (!this.validation.reset().validate().isValid) {
      return
    }

    const saved = await this._teamStore
      .updateTeam(this.team.id, {
        name: this.name,
        teamPurpose: this.teamPurpose,
        address: {
          streetAddress: this.streetAddress,
          zipCode: this.zipCode,
          city: this.city,
          state: this.state,
          country: this.country,
        },
      })
      .then(
        () => true,
        (err: unknown) => {
          if (getFieldErrors(err).length === 0) {
            return this._createAlertErrorHandler('Saving team failed')(err)
          }
          // The form shows the failure on the offending fields.
          populateValidationContextFromError(this.validation, err, 'camel')
          return false
        },
      )
    if (!saved) {
      return
    }

    this._newAlert()
      .withState('success')
      .saying('Success!', 'Your changes were saved!')
      .autoDismiss()
      .show()
  })
}
